/**
 * Mensure Operational Design tokens.
 *
 * These tokens define the sober, analytical visual language used by the app.
 * Keep expressive effects for branded/public surfaces and special product states.
 */

export const operationalRadii = {
  none: 0,
  control: 8,
  surface: 8,
  modal: 8,
  pill: 999,
};

export const operationalSizing = {
  compact: 36,
  default: 40,
  large: 48,
  header: 56,
};

export const operationalSpacing = {
  xs: 0.5,
  sm: 0.75,
  md: 1,
  lg: 1.5,
  xl: 2,
  '2xl': 3,
  '3xl': 4,
};

export const operationalFontSizes = {
  xs: '0.75rem',
  sm: '0.8125rem',
  md: '0.875rem',
  lg: '1rem',
  xl: '1.125rem',
  '2xl': '1.25rem',
  '3xl': '1.5rem',
};

export const operationalSurfaces = {
  dark: {
    shell: '#202124',
    stage: '#292a2d',
    panel: '#292a2d',
    panelRaised: '#303134',
    border: '#3c4043',
    hover: 'rgba(232, 234, 237, 0.08)',
    selected: 'rgba(255, 255, 255, 0.12)',
  },
  light: {
    shell: '#E6EBEE',
    stage: '#ffffff',
    panel: '#ffffff',
    panelRaised: '#f7f9fa',
    border: 'rgba(15, 23, 42, 0.12)',
    hover: 'rgba(15, 23, 42, 0.04)',
    selected: 'rgba(15, 23, 42, 0.08)',
  },
};

export const operationalElevation = {
  none: 'none',
  surface: '0 1px 2px rgba(0, 0, 0, 0.20)',
  raised: '0 8px 18px rgba(0, 0, 0, 0.18)',
  modal: '0 18px 48px rgba(0, 0, 0, 0.32)',
};

export const operationalMotion = {
  quick: '120ms ease',
  standard: '180ms ease',
};

export const operationalScrollbars = {
  default: {
    '&::-webkit-scrollbar': {
      width: 8,
      height: 8,
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'rgba(148, 163, 184, 0.42)',
      borderRadius: operationalRadii.pill,
    },
    '&::-webkit-scrollbar-thumb:hover': {
      backgroundColor: 'rgba(148, 163, 184, 0.62)',
    },
  },
  thin: {
    '&::-webkit-scrollbar': {
      width: 6,
      height: 6,
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'rgba(148, 163, 184, 0.45)',
      borderRadius: operationalRadii.pill,
    },
    '&::-webkit-scrollbar-thumb:hover': {
      backgroundColor: 'rgba(148, 163, 184, 0.65)',
    },
  },
};

export const operationalSnackbar = {
  anchorOrigin: {
    vertical: 'bottom',
    horizontal: 'center',
  },
  autoHideDuration: 4000,
};

export const operationalText = {
  dark: {
    primary: '#f8fafc',
    muted: '#94a3b8',
    mutedStrong: 'rgba(226, 232, 240, 0.82)',
    date: 'rgba(203, 213, 225, 0.66)',
    metric: 'rgba(203, 213, 225, 0.70)',
    quiet: 'rgba(148, 163, 184, 0.68)',
  },
  light: {
    primary: '#111827',
    muted: '#6b7280',
    mutedStrong: '#374151',
    date: '#6f7783',
    metric: '#5c6672',
    quiet: '#9aa4af',
  },
};

export const operationalTypography = {
  articleCard: {
    title: {
      fontSize: '0.82rem',
      lineHeight: 1.16,
      minHeight: '1.9rem',
      fontWeight: 700,
      marginBottom: '7px',
    },
    source: {
      fontSize: '0.76rem',
      fontWeight: 620,
    },
    date: {
      fontSize: '0.73rem',
      fontWeight: 560,
      opacity: 0.86,
      marginTop: '4px',
    },
    tag: {
      height: 18,
      fontSize: '0.56rem',
      fontWeight: 610,
      deleteIconSize: '0.6rem',
    },
    metric: {
      fontSize: '0.71rem',
      iconSize: '0.78rem',
      fontWeight: 620,
      primaryWeight: 650,
    },
  },
  selectionToolbar: {
    iconSize: '1.2rem',
    progressFontSize: '0.85rem',
    counterFontSize: '0.875rem',
  },
};

export const operationalToolbars = {
  selection: {
    dark: {
      surface: '#f8fafc',
      text: '#202124',
      border: 'rgba(255, 255, 255, 0.82)',
      hover: 'rgba(32, 33, 36, 0.08)',
      selected: '#0f1115',
      selectedText: '#f8fafc',
      divider: 'rgba(32, 33, 36, 0.14)',
    },
    light: {
      surface: '#202124',
      text: '#f8fafc',
      border: 'rgba(255, 255, 255, 0.12)',
      hover: 'rgba(248, 250, 252, 0.10)',
      selected: '#f8fafc',
      selectedText: '#202124',
      divider: 'rgba(248, 250, 252, 0.16)',
    },
    iconButton: {
      width: 34,
      height: 34,
    },
    counter: {
      minWidth: 30,
      height: 30,
    },
    prompt: {
      minHeight: 46,
    },
  },
};

export const operationalModal = {
  backdrop: {
    dark: 'rgba(9, 11, 14, 0.76)',
    light: 'rgba(15, 23, 42, 0.34)',
  },
  shadow: {
    dark: '0 18px 42px rgba(0, 0, 0, 0.38)',
    light: '0 18px 42px rgba(15, 23, 42, 0.14)',
  },
};

export const operationalFeedback = {
  alert: {
    radius: operationalRadii.control,
    borderWidth: 1,
    accentBorderWidth: 3,
  },
};

export const operationalStatusColors = {
  info: '#2196f3',
  success: '#4caf50',
  warning: '#ff9800',
  error: '#f44336',
};

export const operationalBrandColors = {
  whatsapp: '#25D366',
  telegram: '#229ED9',
  twitter: '#111827',
  facebook: '#1877F2',
  linkedin: '#0A66C2',
  youtube: '#FF0000',
  email: '#64748b',
};

export const operationalComponentSurfaces = {
  stage: 'var(--layout-stage-bg, var(--background-paper))',
  media: {
    logo: '#ffffff',
    placeholder: '#f0f0f0',
    placeholderRaised: '#e5e5e5',
    dark: '#10151c',
    light: '#e5e7eb',
  },
  chip: {
    dark: {
      background: 'rgba(255, 255, 255, 0.08)',
      backgroundHover: 'rgba(255, 255, 255, 0.12)',
      text: 'rgba(226, 232, 240, 0.70)',
      textHover: 'rgba(248, 250, 252, 0.86)',
    },
    light: {
      background: 'rgba(0, 0, 0, 0.06)',
      backgroundHover: 'rgba(0, 0, 0, 0.10)',
      text: '#5b6470',
      textHover: '#333333',
    },
  },
  filterPanel: {
    dark: 'rgba(255, 255, 255, 0.02)',
    light: 'rgba(0, 0, 0, 0.02)',
    darkHover: 'rgba(30, 30, 30, 0.75)',
    lightHover: 'rgba(255, 255, 255, 0.75)',
    darkHoverBorder: 'rgba(255, 255, 255, 0.15)',
    lightHoverBorder: 'rgba(0, 0, 0, 0.15)',
  },
  mediaControl: {
    background: 'rgba(0, 0, 0, 0.70)',
    backgroundHover: 'rgba(0, 0, 0, 0.85)',
    hover: 'rgba(0, 0, 0, 0.35)',
    hoverStrong: 'rgba(0, 0, 0, 0.45)',
  },
};

export const getOperationalMode = (isDarkModeOrMode) => {
  if (isDarkModeOrMode === 'dark' || isDarkModeOrMode === true) return 'dark';
  return 'light';
};

export const getOperationalText = (isDarkModeOrMode) => (
  operationalText[getOperationalMode(isDarkModeOrMode)]
);

export const getSelectionToolbarTokens = (isDarkModeOrMode) => (
  operationalToolbars.selection[getOperationalMode(isDarkModeOrMode)]
);

const operationalTokens = {
  radii: operationalRadii,
  sizing: operationalSizing,
  spacing: operationalSpacing,
  fontSizes: operationalFontSizes,
  surfaces: operationalSurfaces,
  elevation: operationalElevation,
  motion: operationalMotion,
  scrollbars: operationalScrollbars,
  snackbar: operationalSnackbar,
  text: operationalText,
  typography: operationalTypography,
  toolbars: operationalToolbars,
  modal: operationalModal,
  feedback: operationalFeedback,
  statusColors: operationalStatusColors,
  brandColors: operationalBrandColors,
  componentSurfaces: operationalComponentSurfaces,
};

export default operationalTokens;
